import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";

import MgButton from "../button/MgButton";
import MgInput from "../input/MgInput";
import { Common } from "../../styles/Common";

import { useMatchSmsVerificationCode, useSendSmsVerification } from "../../api/smsVerification/smsVerificationApi";
import { useSmsVerificationInfoStore } from "../../store/smsVerificationStore";

interface Props {
    onMatchSuccess: (phone?: string) => void;
    submitButtonText?: string;
    onLoginWithEmail: () => void;
}

const MAX_VERIFICATION_CODE_LENGTH = 6;

// 에러메시지
const ERROR_MESSAGES: Record<string, string> = {
    INVALID_LENGTH: "인증번호는 6자리 숫자로 입력해주세요.",
    NOT_NUMERIC: "숫자만 입력해주세요.",
    EMPTY: "인증번호를 입력해주세요.",
    WRONG_CODE: "인증번호가 일치하지 않아요. 다시 확인해주세요.",
    EXPIRED: "입력 시간이 만료되었어요. 다시 요청해주세요.",
    INVALID_PHONE: "휴대전화 번호를 올바르게 입력해주세요",
    LIMITED: "5초 후 재인증 가능합니다.",
};

const SMSInputBox = ({ onMatchSuccess, submitButtonText, onLoginWithEmail }: Props) => {
    const [smsSentCount, setSmsSentCount] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
    const [isLimited, setIsLimited] = useState(false);

    const { mutate: matchSmsVerificationCode } = useMatchSmsVerificationCode();
    const { smsVerificationInfo, setSmsVerificationInfo } = useSmsVerificationInfoStore();
    const { mutate: sendSmsVerification } = useSendSmsVerification();

    const verificationCodeInputRef = useRef<HTMLInputElement>(null);

    // 코드 유효성 검사 함수
    const validateCode = (code: string): string | null => {
        if (!code) return "EMPTY";
        if (!/^\d+$/.test(code)) return "NOT_NUMERIC";
        if (code.length !== MAX_VERIFICATION_CODE_LENGTH) return "INVALID_LENGTH";
        return null;
    };

    const handleChangePhone = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value.length === 0) {
            setSmsVerificationInfo({ ...smsVerificationInfo, phone: "" });
            setErrorMessage("");
            return;
        }

        if (value.startsWith("@") || value.startsWith("t")) {
            setSmsVerificationInfo({ ...smsVerificationInfo, phone: value });
            setErrorMessage("");
            return;
        }
        if (value.length > 11) {
            return;
        }

        setSmsVerificationInfo({ ...smsVerificationInfo, phone: e.target.value });
        setErrorMessage("");
    };

    const handleSendSmsVerification = () => {
        if (!/^\d+$/.test(smsVerificationInfo.phone)) {
            onLoginWithEmail();
        } else {
            if (isLimited) {
                setErrorMessage("LIMITED");
                return;
            }

            setIsLimited(true);

            sendSmsVerification(smsVerificationInfo.phone, {
                onSuccess: () => {
                    setSmsSentCount((prev) => prev + 1);
                    setTimeRemaining(60 * 3); // 3분 타이머 시작

                    setTimeout(() => {
                        verificationCodeInputRef.current?.focus();
                    }, 100);
                },
                onError: (error) => {
                    console.log(error);
                },
            });
        }
    };

    // 인증번호 입력 핸들러 (6자리 초과 시 경고)
    const handleChangeVerificationCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        // 숫자만 입력 가능
        if (!/^\d*$/.test(value)) {
            setErrorMessage("NOT_NUMERIC");
            return;
        }

        // 6자리 초과 시 경고
        if (value.length > MAX_VERIFICATION_CODE_LENGTH) {
            setErrorMessage("INVALID_LENGTH");
        } else {
            setErrorMessage("");
        }

        // 실제 상태에는 6자리까지만 반영
        setSmsVerificationInfo({
            ...smsVerificationInfo,
            code: value.slice(0, MAX_VERIFICATION_CODE_LENGTH),
        });
    };

    const handleMatchSmsVerificationCode = () => {
        const validationError = validateCode(smsVerificationInfo.code);
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        if (timeRemaining === 0) {
            setErrorMessage("EXPIRED");
            return;
        }

        matchSmsVerificationCode(
            { phone: smsVerificationInfo.phone, code: smsVerificationInfo.code },
            {
                onSuccess: (data) => {
                    if (data === true) {
                        onMatchSuccess(smsVerificationInfo.phone);
                    }
                },
                onError: (error) => {
                    const errCode = (error as any).response.data?.code;
                    setErrorMessage(errCode || "WRONG_CODE");
                },
            },
        );
    };

    const buttonText = useMemo(() => (smsSentCount > 0 ? "재인증" : "인증요청"), [smsSentCount]);

    const isButtonDisabled = useMemo(
        () => smsSentCount === 0 || smsVerificationInfo.code.length !== MAX_VERIFICATION_CODE_LENGTH,
        [smsSentCount, smsVerificationInfo.code],
    );

    useEffect(() => {
        if (smsVerificationInfo.code.length === MAX_VERIFICATION_CODE_LENGTH) {
            setTimeout(() => {
                verificationCodeInputRef.current?.blur();
            }, 300);
        }
    }, [smsVerificationInfo.code]);

    // 타이머 제한
    useEffect(() => {
        if (isLimited) {
            const timer = setTimeout(() => {
                setIsLimited(false);
                setErrorMessage("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [isLimited]);

    // 인증 타이머 감소
    useEffect(() => {
        if (timeRemaining === null) return;

        if (timeRemaining === 0) {
            setErrorMessage("EXPIRED");
            return;
        }

        const interval = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev && prev > 0) return prev - 1;
                return 0;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [timeRemaining]);

    return (
        <ContainerStyle>
            <div className="flexColBox">
                <div className="flexBox">
                    <MgInput
                        width="100%"
                        value={smsVerificationInfo.phone}
                        onChange={handleChangePhone}
                        placeholder="01012345678"
                        // isDisabled={smsSentCount > 0}
                        onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) => {
                            if (evt.key === "Enter") {
                                handleSendSmsVerification();
                            }
                        }}
                    />
                    <MgButton
                        height="51px"
                        width="100%"
                        onClick={handleSendSmsVerification}
                        addStyles={loginBtnStyle}
                        text={buttonText}
                        isDisabled={!isButtonDisabled}
                    />
                </div>
                <div className="flexBox verificationCodeBox">
                    <MgInput
                        width="100%"
                        value={smsVerificationInfo.code}
                        onChange={handleChangeVerificationCode}
                        placeholder="인증 번호 입력"
                        type="text"
                        inputRef={verificationCodeInputRef}
                        inputmode="numeric"
                        pattern="\d{6}"
                        autocomplete="one-time-code"
                        // isDisabled={smsSentCount < 1}
                        onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) => {
                            if (evt.key === "Enter") {
                                handleMatchSmsVerificationCode();
                            }
                        }}
                    />
                    {smsSentCount > 0 && timeRemaining !== null && (
                        <TimerStyle>{`${Math.floor(timeRemaining / 60)}:${(timeRemaining % 60)
                            .toString()
                            .padStart(2, "0")}`}</TimerStyle>
                    )}

                    <ErrorTextStyle>
                        {/* {smsSentCount > 0 && (
                            <ErrorTextChangePhone
                                onClick={() => {
                                    setSmsVerificationInfo({ phone: "", code: "" });
                                    setSmsSentCount(0);
                                    setErrorMessage("");
                                }}
                            >
                                전화번호 변경
                            </ErrorTextChangePhone>
                        )} */}
                        {errorMessage.length > 0 && (ERROR_MESSAGES[errorMessage] || "알 수 없는 오류입니다.")}{" "}
                    </ErrorTextStyle>
                </div>
                <MgButton
                    width="100%"
                    onClick={handleMatchSmsVerificationCode}
                    addStyles={loginBtn}
                    text={submitButtonText || "로그인 (회원가입)"}
                    isDisabled={isButtonDisabled}
                />
            </div>
        </ContainerStyle>
    );
};

export default SMSInputBox;

const TimerStyle = styled.div`
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: ${Common.colors.error};
    font-size: 12px;
    z-index: 1;

    @media (max-width: 768px) {
        right: 12px;
        font-size: 11px;
    }
`;

const ContainerStyle = styled.div`
    position: relative;
    width: 100%;
    min-height: 12px;
    margin: 8px 0px 24px;

    .flexColBox {
        display: flex;
        gap: 8px;
        flex-direction: column;
    }

    .flexBox {
        display: flex;
        gap: 8px;
    }

    .verificationCodeBox {
        position: relative;
    }
`;

const loginBtnStyle = css`
    border-radius: 8px;
    border: none;
    background: #2275ee;
    color: #ffffff;
    font-size: 14px;
    max-width: 88px;

    &&:disabled {
        background: #d9d9d9 !important;
        color: #ffffff !important;
    }
`;

const loginBtn = css`
    border-radius: 8px;
    border: none;
    background: #2275ee;
    color: #ffffff;
    font-size: 16px;
    max-width: 100%;
    margin-top: 70px;
    &&:disabled {
        background: #d9d9d9 !important;
        color: #ffffff !important;
    }
`;

const ErrorTextStyle = styled.p`
    position: absolute;
    bottom: -20px;
    left: 4px;
    color: ${Common.colors.error};
    font-size: 12px;
    margin-top: 4px;
    width: fit-content;
    display: flex;
    gap: 6px;
`;

const ErrorTextChangePhone = styled.div`
    color: ${Common.colors.black};
    cursor: pointer;

    &&:hover {
        text-decoration: underline;
    }
`;
